/**
 * PediatricOralCard — педиатрична орална превенция (AAP/AAPD).
 * Дете, оценка на риска, паста по възраст, флуорид, календар и съвети.
 * Данни: /api/dental-intelligence/pediatric (+ /child)
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Baby, Plus, Trash2, Loader2, CalendarClock, Info } from "lucide-react";
import { useAuth } from "@/App";

const LEVEL = {
  low: { color: "#10B981", label: "нисък риск" },
  moderate: { color: "#F59E0B", label: "среден риск" },
  high: { color: "#EF4444", label: "висок риск" },
};

export default function PediatricOralCard({ isDark = false, cardCls = "" }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dental-intelligence/pediatric`);
      setD(data);
    } catch { setD(null); }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const border = isDark ? "border-white/10" : "border-gray-200";
  const inputCls = `px-2 py-1.5 rounded-lg border text-[12px] ${
    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`;

  const startNew = (child) => setForm(child
    ? { ...child, factors: child.risk ? undefined : child.factors }
    : { name: "", birth_date: "", factors: [], water_ppm: "" });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/dental-intelligence/pediatric/child`, {
        child_id: form.child_id || null,
        name: form.name || "дете",
        birth_date: form.birth_date,
        factors: form.factors || [],
        water_ppm: form.water_ppm === "" ? null : Number(form.water_ppm),
      });
      setD(data); setForm(null);
      toast.success("Планът за детето е готов");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не се записа");
    } finally { setBusy(false); }
  };

  const remove = async (cid) => {
    try {
      const { data } = await axios.delete(`${API}/dental-intelligence/pediatric/child/${cid}`);
      setD(data); toast.success("Изтрито");
    } catch { toast.error("Неуспешно"); }
  };

  const toggleFactor = (key) => setForm((f) => ({
    ...f,
    factors: (f.factors || []).includes(key)
      ? f.factors.filter((k) => k !== key)
      : [...(f.factors || []), key],
  }));

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="dhi-pediatric-card">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <Baby className="w-4 h-4 text-cyan-500" /> Педиатрична орална превенция
          </div>
          <p className="text-[11px] opacity-70 mt-0.5">{d.intro_bg}</p>
        </div>
        <button onClick={() => startNew(null)}
          className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-white
                     bg-gradient-to-r from-cyan-500 to-sky-600 inline-flex items-center gap-1"
          data-testid="dhi-pediatric-add">
          <Plus className="w-3.5 h-3.5" /> Добави дете
        </button>
      </div>

      {form && (
        <div className={`mt-3 p-3 rounded-xl border ${border} space-y-2`} data-testid="dhi-pediatric-form">
          <div className="flex flex-wrap gap-2">
            <input placeholder="Име" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls} data-testid="dhi-pediatric-name" />
            <input type="date" value={form.birth_date}
              onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
              className={inputCls} data-testid="dhi-pediatric-birth" />
            <input type="number" step="0.1" placeholder="Флуорид във водата (ppm)"
              value={form.water_ppm}
              onChange={(e) => setForm({ ...form, water_ppm: e.target.value })}
              className={`${inputCls} w-44`} data-testid="dhi-pediatric-ppm" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase opacity-60 mb-1">Рискови фактори</div>
            <div className="flex flex-wrap gap-1">
              {d.risk_factors.map((f) => (
                <button key={f.key} onClick={() => toggleFactor(f.key)}
                  className={`px-2 py-1 rounded-full text-[10px] font-bold border transition
                    ${(form.factors || []).includes(f.key)
                      ? "border-rose-500 bg-rose-500/15 text-rose-500" : `${border} opacity-70`}`}
                  data-testid={`dhi-pediatric-risk-${f.key}`}>
                  {f.label_bg}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase opacity-60 mb-1">Защитни фактори</div>
            <div className="flex flex-wrap gap-1">
              {d.protective_factors.map((f) => (
                <button key={f.key} onClick={() => toggleFactor(f.key)}
                  className={`px-2 py-1 rounded-full text-[10px] font-bold border transition
                    ${(form.factors || []).includes(f.key)
                      ? "border-emerald-500 bg-emerald-500/15 text-emerald-600" : `${border} opacity-70`}`}
                  data-testid={`dhi-pediatric-prot-${f.key}`}>
                  {f.label_bg}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={busy || !form.birth_date}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white
                         bg-gradient-to-r from-cyan-500 to-sky-600 disabled:opacity-50
                         inline-flex items-center gap-1"
              data-testid="dhi-pediatric-save">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Изчисли плана
            </button>
            <button onClick={() => setForm(null)}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold opacity-70 border"
              data-testid="dhi-pediatric-cancel">Отказ</button>
          </div>
        </div>
      )}

      {d.children.map((c) => {
        const lv = LEVEL[c.risk.level];
        return (
          <div key={c.child_id} className={`mt-3 p-3 rounded-xl border ${border}`}
            data-testid={`dhi-pediatric-child-${c.child_id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[12px] font-extrabold">
                {c.name} · {c.age_bg}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: lv.color + "22", color: lv.color }}
                  data-testid={`dhi-pediatric-risk-level-${c.child_id}`}>
                  {lv.label}
                </span>
                <button onClick={() => remove(c.child_id)} className="opacity-50 hover:opacity-100"
                  data-testid={`dhi-pediatric-delete-${c.child_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div className="mt-2 grid sm:grid-cols-2 gap-2 text-[11px]">
              <div className={`p-2 rounded-lg border ${border}`} data-testid={`dhi-pediatric-paste-${c.child_id}`}>
                <div className="font-bold">Паста: {c.toothpaste.amount_bg}</div>
                <div className="opacity-70">{c.toothpaste.detail_bg}</div>
              </div>
              <div className={`p-2 rounded-lg border ${border}`} data-testid={`dhi-pediatric-fluoride-${c.child_id}`}>
                <div className="font-bold">
                  Флуоридна добавка: {c.fluoride_supplement.indicated === true ? "показана"
                    : c.fluoride_supplement.indicated === false ? "не е нужна" : "неясно"}
                </div>
                <div className="opacity-70">{c.fluoride_supplement.note_bg}</div>
              </div>
            </div>

            {!!c.schedule.length && (
              <div className="mt-2">
                <div className="text-[10px] font-bold uppercase opacity-60 inline-flex items-center gap-1">
                  <CalendarClock className="w-3.5 h-3.5" /> Календар
                </div>
                <div className="mt-1 space-y-1">
                  {c.schedule.map((s, i) => (
                    <div key={i} className="text-[11px]" data-testid={`dhi-pediatric-sched-${c.child_id}-${i}`}>
                      <b>{s.when_bg}:</b> {s.what_bg}
                      <div className="opacity-60 text-[10px]">{s.why_bg}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-2 space-y-1">
              {c.guidance.map((g, i) => (
                <div key={i} className="text-[11px] flex items-start gap-1.5"
                  data-testid={`dhi-pediatric-tip-${c.child_id}-${i}`}>
                  <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-cyan-500" />
                  <span><b>{g.title_bg}:</b> {g.text_bg}</span>
                </div>
              ))}
            </div>

            {!!c.risk.risk_labels_bg.length && (
              <div className="mt-2 text-[10px] opacity-70">
                Отчетени рискове: {c.risk.risk_labels_bg.join(" · ")}
              </div>
            )}
          </div>
        );
      })}

      <div className="text-[9px] opacity-50 mt-3">{d.note_bg}</div>
    </div>
  );
}
